/**
 * This is an API client for all checkout-related requests. It replaces now deprecated `useCheckoutRequest`.
 *
 * Depending on the checkout version, it performs Inertia.js requests directly,
 * or `fetch` requests by calling `SpaApiEmbedClient` via an iframe.
 *
 * The latter is implemented to comply with the security CORS requirements,
 * as cross-origin requests should only be made from a trusted origin.
 */

import { type Connection, type Methods as BaseMethods } from 'penpal'

import { useCheckoutState } from '@/Pages/Web/Components/Checkout/useCheckoutState.js'
import { Inertia, type VisitOptions } from '@inertiajs/inertia'
import route from '@/Util/route'
const { setState: updateCheckout, isReady } = useCheckoutState()

type Method = 'get' | 'post' | 'patch' | 'delete'
type Cart = Record<string, any>
type Response = Promise<Record<string, any>>

/**
 * `SpaApiEmbedClient` methods
 */
interface Methods extends BaseMethods {
  setup(cart: any, isApi: boolean, storeDomain: string): Response
  getCurrentUser(cartId: string): Response
  addDiscount(code: string, service: string): Response
  removeDiscount(): Response
  setLocale(locale: string): Response
  saveEmail(email: string): Response
  confirmEmail(email: string): Response
  validate(payload: Record<string, any>, service: string): Response
  updateCartItem(payload: Record<string, any>): Response
  calculateTax(payload: Record<string, any>): Response
  requestTaxExemptLink(): Response
  trackVisit(visitorId: string, affId: string, url: string): Response
  getFulfilmentStatus(): Response
  authorizePayment(): Response
  submitCheckout(payload: Record<string, any>): Response
  createPayPalOrder(cartId: string, country: string, postalCode: number | string): Response
  getPayPalSubscriptionPlan(cartId: string): Response
  createPayPalSubscription(cartId: string, urlDomain: string, country: string, postalCode: string | number): Response
  storePayPalSubscription(cartId: string, orderId: string, subscriptionId: string): Response
  setupCart(
    variant?: string,
    cart?: string | null,
    query?: any,
    country?: string | null,
    referer?: string | null,
    trackingId?: string,
  ): Response
}

interface InertiaRequestOptions {
  method: Method
  body?: Record<string, any>
}

/**
 * Inertia.js checkout client
 */
class SpaApiInertiaClient {
  private cart: Cart
  private storeDomain: string

  constructor(cart: Cart, storeDomain: string) {
    this.cart = cart
    this.storeDomain = storeDomain
  }

  request(url: string, options: InertiaRequestOptions) {
    return new Promise((resolve, reject) => {
      const method = options.method.toLowerCase() as Method

      const params: Exclude<VisitOptions, 'method'> = {
        headers: {
          'X-Secret': this.cart.secret ?? '',
        },
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => resolve(page),
        onError: (errors) => reject(errors),
      }

      if (method === 'delete') {
        Inertia.delete(url, params)
      } else {
        Inertia[method](url, options.body, params)
      }
    })
  }

  route(name: string, params = {}) {
    return route(name, {
      domain: this.storeDomain,
      cart: this.cart.id,
      ...params,
    })
  }

  setup(cart: Record<string, any>) {
    // This is left blank intentionally to
  }

  addDiscount(code: string, service: string) {
    return this.request(this.route('checkout.add-discount', { type: service }), {
      method: 'post',
      body: { discount_code: code },
    })
  }

  removeDiscount() {
    return this.request(this.route('checkout.remove-discount'), {
      method: 'delete',
    })
  }

  setLocale(locale: string) {
    return this.request(this.route('checkout.locale'), {
      method: 'post',
      body: { locale },
    })
  }

  confirmEmail(email: string) {
    return this.request(this.route('checkout.confirm-email'), {
      method: 'post',
      body: { email },
    })
  }

  validate(payload: Record<string, any>, service: string) {
    return this.request(this.route('checkout.validate', { type: service }), {
      method: 'post',
      body: payload,
    })
  }

  updateCartItem(payload: Record<string, any>) {
    return this.request(this.route('checkout.update'), {
      method: 'post',
      body: payload,
    })
  }

  calculateTax(payload: Record<string, any>) {
    return this.request(this.route('checkout.tax'), {
      method: 'post',
      body: payload,
    })
  }

  requestTaxExemptLink() {
    return this.request(this.route('checkout.tax-exempt.redirect'), {
      method: 'post',
    })
  }
}

class SpaApiClient {
  private connection?: Promise<Connection<Methods>>
  private inertiaRequest?: SpaApiInertiaClient
  private isApi?: boolean

  constructor() {
    if (typeof window !== 'undefined') {
      this.connection = this.connect()
    }
  }

  async connect() {
    const { connect, WindowMessenger } = await import('penpal')

    const iframe = document.createElement('iframe')

    const iframeUrl = import.meta.env.VITE_IFRAME_API_CLIENT_URL
    const allowedOrigin = import.meta.env.VITE_IFRAME_API_CLIENT_ALLOWED_ORIGIN

    if (!allowedOrigin || !iframeUrl) {
      throw new Error('Incorrect configuration for iframe API client')
    }

    iframe.src = iframeUrl
    iframe.classList.add('sr-only')

    document.body.appendChild(iframe)

    const messenger = new WindowMessenger({
      remoteWindow: iframe.contentWindow!,
      allowedOrigins: [allowedOrigin],
    })

    return connect<Methods>({
      messenger
    })
  }

  /**
   * Returns either a promise for an iframe API client, or an Inertia request instance
   */
  request() {
    if (this.isApi) {
      return this.apiRequest()
    }

    return this.inertiaRequest!
  }

  async apiRequest() {
    const connection = await this.connection

    return await connection!.promise
  }

  async setup(isApi: boolean, storeDomain: string, cart: any) {
    this.isApi = isApi

    if (!isApi) {
      this.inertiaRequest = new SpaApiInertiaClient(cart, storeDomain)
    }

    return (await this.apiRequest()).setup(JSON.parse(JSON.stringify(cart)), this.isApi, storeDomain)
  }

  updateCheckout(data: Record<string, any>) {
    // Inertia state is updated automatically with props change.
    if (!this.isApi) {
      return
    }

    updateCheckout(data)
  }

  async getCurrentUser(cartId: string) {
    // Force use the iframe API client
    return (await this.apiRequest()).getCurrentUser(cartId)
  }

  async addDiscount(code: string, service: string) {
    if (!isReady) {
      return
    }

    return (await this.request()).addDiscount(code, service).then((response: any) => this.updateCheckout(response.data))
  }

  async removeDiscount() {
    if (!isReady) {
      return
    }

    return (await this.request()).removeDiscount().then((response: any) => this.updateCheckout(response.data))
  }

  async setLocale(locale: string) {
    if (!isReady) {
      return
    }

    return (await this.request()).setLocale(locale).then((response: any) => this.updateCheckout(response.data))
  }

  async saveEmail(email: string) {
    if (!isReady) {
      return
    }

    // Force use the iframe API client
    return (await this.apiRequest()).saveEmail(email).then((response: any) => this.updateCheckout(response.data))
  }

  async confirmEmail(email: string) {
    if (!isReady) {
      return
    }

    return (await this.request()).confirmEmail(email).then((response: any) => this.updateCheckout(response.data))
  }

  async validate(payload: Record<string, any>, service: string) {
    if (!isReady) {
      return
    }

    return (await this.request()).validate(payload, service).then((response: any) => this.updateCheckout(response.data))
  }

  async updateCartItem(payload: Record<string, any>) {
    if (!isReady) {
      return
    }

    return (await this.request()).updateCartItem(payload).then((response: any) => this.updateCheckout(response.data))
  }

  async calculateTax(payload: Record<string, any>) {
    if (!isReady) {
      return
    }

    return (await this.request()).calculateTax(payload).then((response: any) => this.updateCheckout(response.data))
  }

  async requestTaxExemptLink() {
    if (!isReady) {
      return
    }

    return (await this.request()).requestTaxExemptLink().then((response: any) => this.updateCheckout(response.data))
  }

  async trackVisit(visitorId: string, affId: string, url: string) {
    return (await this.apiRequest()).trackVisit(visitorId, affId, url)
  }

  async getFulfilmentStatus() {
    return (await this.apiRequest()).getFulfilmentStatus()
  }

  async authorizePayment() {
    return (await this.apiRequest()).authorizePayment()
  }

  async submitCheckout(payload: Record<string, any>) {
    return (await this.apiRequest()).submitCheckout(payload)
  }

  async createPayPalOrder(cartId: string, country: string, postalCode: number | string) {
    return (await this.apiRequest()).createPayPalOrder(cartId, country, postalCode)
  }

  async getPayPalSubscriptionPlan(cartId: string) {
    return (await this.apiRequest()).getPayPalSubscriptionPlan(cartId)
  }

  async createPayPalSubscription(cartId: string, urlDomain: string, country: string, postalCode: string | number) {
    return (await this.apiRequest()).createPayPalSubscription(cartId, urlDomain, country, postalCode)
  }

  async storePayPalSubscription(cartId: string, orderId: string, subscriptionId: string) {
    return (await this.apiRequest()).storePayPalSubscription(cartId, orderId, subscriptionId)
  }

  // Only called from Nuxt's `useCheckout`
  async setupCart(
    variant?: string,
    cart?: string | null,
    query?: any,
    country?: string | null,
    referer?: string | null,
    trackingId?: string,
  ) {
    return (await this.apiRequest()).setupCart(variant, cart, query, country, referer, trackingId)
  }
}

// Export default to make it a singleton
export default new SpaApiClient()
